import { DiscountSources } from '@gamiphy/bl/cashbackDiscount';
import { TierConfigType } from '@gamiphy/bl/level';
import { Points } from '@gamiphy/bl/referrals/types';
import { PopupCtoDtoActionEnum, PopupDto, PopupDtoLocationEnum } from '@coretava/api/core-service';
import Signup from '@gamiphy/loyalty-station-desktop/components/cashback/signup/Signup';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { DiscountsStore } from '@gamiphy/loyalty-station-desktop/store/discounts';
import { GameStore } from '@gamiphy/loyalty-station-desktop/store/game';
import { LevelStore } from '@gamiphy/loyalty-station-desktop/store/level';
import { observer } from 'mobx-react';
import { PopupsStore } from '@gamiphy/loyalty-station-desktop/store/popups';
import { ReferralsStore } from '@gamiphy/loyalty-station-desktop/store/refferals';
import { SurveyStore } from '@gamiphy/loyalty-station-desktop/store/survey';
import { UiStore } from '@gamiphy/loyalty-station-desktop/store/ui';
import { useTranslation } from '@gamiphy/react-i18n';
import { Box, ClickAwayListener, Grid } from '@mui/material';
import React from 'react';
import { SessionStore, SignupSteps } from '@gamiphy/loyalty-station-desktop/store/session';
import CloseButton from './CloseButton';
import { useAutoClose } from './hooks';
import PopupContentBody from './PopupContentBody';
import { getPopupPosition, getPopupSizes } from './utils';

const PopupContent: React.FC<{ popup: PopupDto, isMobile: boolean }> = observer(({
  popup,
  isMobile,
}) => {
  const { t } = useTranslation();
  const { tierConfig } = LevelStore;

  const popupPositionConfig = React.useMemo(() => getPopupPosition(popup, { isMobile }), [popup, isMobile]);
  const popupSizeConfig = React.useMemo(() => getPopupSizes(popup, {
    isMobile,
  }), [popup, isMobile]);

  const onClose = React.useCallback(() => {
    PopupsStore.closePopup();
    PopupsStore.closeJoinProgram();
    if (popup?.hideWidget) {
      UiStore.showBar();
    }
  }, [popup]);

  const showBanner = React.useMemo(() => popup.sideBanner.enabled && popup.sideBanner.background, [popup]);

  const welcomeGiftPoints = React.useMemo(() => {
    if (GameStore.enabled && SurveyStore.active) {
      return (GameStore.appGame?.points?.gems ?? 0) + (SurveyStore.appSurvey?.points?.gems ?? 0);
    }

    if (GameStore.enabled) {
      return GameStore.appGame?.points.gems ?? 0;
    }

    if (SurveyStore.active) {
      return SurveyStore.appSurvey?.points?.gems ?? 0;
    }

    return 0;
  }, [GameStore.appGame?.points.gems, SurveyStore.appSurvey?.points?.gems]);

  const welcomeGiftActions = React.useMemo(() => {
    if (GameStore.enabled && SurveyStore.active) {
      return t('popups.welcomePoints.action.both', 'Answer a survey and play a game');
    }

    if (GameStore.enabled) {
      return t('popups.welcomePoints.action.game', 'Play a game');
    }

    if (SurveyStore.active) {
      return t('popups.welcomePoints.action.survey', 'Answer a survey');
    }

    return '';
  }, [GameStore.enabled, SurveyStore.active]);

  const getPointsString = (points?: Points): string | undefined => React.useMemo(() => {
    if (!points) {
      return undefined;
    }

    if (points.gems && points.exp && tierConfig?.type === TierConfigType.exp) {
      return t('referral.points', '{{gems, gems}} and {{exp, exp}}', {
        gems: points.gems,
        exp: points.exp,
      });
    }
    if (points.gems) {
      return t('referral.points.gems', '{{gems, gems}}', {
        gems: points.gems,
      });
    }
    if (points.exp && tierConfig?.type === TierConfigType.exp) {
      return t('referral.points.exp', '{{exp, exp}}', {
        exp: points.exp,
      });
    }

    return undefined;
  }, [points, tierConfig]);

  const signUpPoints = getPointsString(ReferralsStore.appReferral?.referrerPoints?.invite);
  const purchasePoints = getPointsString(ReferralsStore.appReferral?.referrerPoints?.purchase);

  const referralReward = React.useMemo(() => {
    if (signUpPoints && purchasePoints) {
      return t('popups.referral.body.signUpAndPurchase', '{{signUpPoints}} when they signup and {{purchasePoints}} when they make a purchase.', {
        signUpPoints,
        purchasePoints,
      });
    }
    if (signUpPoints) {
      return t('popups.referral.body.signUp', '{{signUpPoints}} when they signup.', {
        signUpPoints,
      });
    }
    if (purchasePoints) {
      return t('popups.referral.body.purchase', '{{purchasePoints}} when they make a purchase.', {
        purchasePoints,
      });
    }
    return undefined;
  }, [signUpPoints, purchasePoints]);

  const i18nTagsValues = React.useMemo(() => ({
    referrerPoints: ReferralsStore.appReferral?.referrerPoints?.purchase.gems,
    referrerLevelPoints: tierConfig?.type === TierConfigType.exp ? ReferralsStore.appReferral?.referrerPoints?.purchase.exp : '',
    refereePoints: ReferralsStore.appReferral?.refereePoints?.purchase.gems,
    refereeLevelPoints: tierConfig?.type === TierConfigType.exp ? ReferralsStore.appReferral?.refereePoints?.purchase.exp : '',
    appName: AppStore.app.name,
    welcomeGiftPoints: welcomeGiftPoints > 0 ? welcomeGiftPoints : '',
    welcomeGiftActions,
    referralReward,
  }), [ReferralsStore.appReferral, tierConfig, AppStore.app, welcomeGiftPoints, welcomeGiftActions, referralReward]);

  useAutoClose(popup);

  const discountCode = React.useMemo<string | undefined>(() => {
    if (popup?.cta.action === PopupCtoDtoActionEnum.CopyCode) {
      if (popup.cta.discountCode) {
        return popup.cta.discountCode;
      }
      if (DiscountsStore.bestFitDiscount?.applicableDiscount.discount?.source === DiscountSources.WelcomeGift) {
        return DiscountsStore.bestFitDiscount.applicableDiscount.discount.code;
      }
    }
    return undefined;
  }, [popup, DiscountsStore.bestFitDiscount, AppStore.app]);

  React.useEffect(
    () => {
      if (popup?.hideWidget && PopupsStore.opened && !UiStore.barHidden) {
        UiStore.hideBar();
      }
    },
    [popup, UiStore.barHidden],
  );

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Grid
        container
        justifyContent="center"
        flexDirection={isMobile ? 'column' : 'row'}
        flexWrap="nowrap"
        sx={{
          ...!isMobile && {
            alignItems: 'stretch',
          },
          width: showBanner ? `calc(calc(12 / 6) * ${popupSizeConfig.width})` : popupSizeConfig.width,
          maxWidth: '90%',
          backgroundColor: '#fff',
          borderRadius: '16px',
          ...popupPositionConfig,
          position: (popup?.location !== PopupDtoLocationEnum.Center && !isMobile) ? 'absolute' : 'relative',
          overflow: 'hidden',
          boxShadow: '0 10px 20px 0 rgba(11, 19, 54, 0.03)',
          bgcolor: popup.colors?.bg,
        }}
      >
        <Box
          position="absolute"
          right={8}
          top={8}
          width={popupSizeConfig.closButtonSize}
          height={popupSizeConfig.closButtonSize}
        >
          <CloseButton onClose={onClose} />
        </Box>
        {
          showBanner && (
            <Grid
              size={!isMobile ? 5.5 : undefined}
              sx={{
                ...isMobile && {
                  height: '22rem',
                },
                background: `url(${popup.sideBanner.background})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />
          )
        }
        <Grid
          size={(!isMobile && showBanner) ? 6.5 : undefined}
          width="100%"
          sx={{
            overflow: 'auto',
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            bgcolor={popup.colors.bg}
            height="100%"
          >
            {
              PopupsStore.showJoinProgram ? (
                <Box textAlign="left" p={popupSizeConfig.padding}>
                  <Signup onDone={() => SessionStore.updateSession({ signupStep: SignupSteps.Success })} />
                </Box>
              )
                : (
                  <PopupContentBody
                    popup={popup}
                    isMobile={isMobile}
                    popupSizeConfig={popupSizeConfig}
                    showBanner={!!showBanner}
                    i18nTagsValues={i18nTagsValues}
                    discountCode={discountCode}
                    onClose={onClose}
                  />
                )
            }
          </Box>
        </Grid>
      </Grid>
    </ClickAwayListener>
  );
});

export default PopupContent;
